import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Virtuoso } from "react-virtuoso";
import { useWindowSize } from "../../hooks/useWindowSize/index.js";
import { Bulkbar } from "../Bulkbar/Bulkbar.js";
import { Checkbox } from "../Checkbox/Checkbox.js";
import { Spinner } from "../Spinner/Spinner.js";
import { StyledList, StyledListContainer, StyledListRow } from "./List.style.js";
import { DefaultListProps, ListProps } from "./List.types.js";

export const List = <Item extends { _id: string }>(props: ListProps<Item>): JSX.Element => {
    const defaultProps: DefaultListProps<Item> = {
        virtualized: false,
        itemSpacing: "16",
        threshold: 0.9,
        loader: (
            <div className="loader">
                <Spinner />
            </div>
        ),
    };

    const {
        loadMore,
        data,
        renderItem,
        header,
        selectedItems,
        onItemsSelection,
        threshold,
        bulkbarContent,
        totalCount,
        showSelection,
        disableSelection,
        selectedItemsTitle,
        disableAllSelection,
        emptyList,
        loader,
        itemSpacing,
        virtualized,
        boxShadowOnRowHover,
        bgColorOnRowHover,
        rowCustomStyle,
        overflowY,
        divider,
        popoverRef,
        noHover = false,
        ...attributes
    } = { ...defaultProps, ...props };

    const containerId = useId();

    const [isShiftDown, setIsShiftDown] = useState<boolean>(false);
    const [lastIndexToggled, setLastIndexToggled] = useState<number>(0);
    const [isAllExclusionMode, setIsAllExclusionMode] = useState<boolean>(false);
    const [hasVerticalScrollbars, setHasScrollbar] = useState<boolean>(true);

    const containerRef = useRef<HTMLDivElement>(null);
    const previousSelectedRef = useRef<ListProps<Item>["selectedItems"]>(undefined);

    const hasMore = totalCount > data.length;
    const windowSize = useWindowSize();

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const virtualizedComponent = container.querySelector(".virtualized-list");
        if (virtualizedComponent) {
            setHasScrollbar((virtualizedComponent.scrollHeight ?? 0) >= (virtualizedComponent.clientHeight ?? 0));
        } else {
            setHasScrollbar((container.scrollHeight ?? 0) >= (container.clientHeight ?? 0));
        }
    }, [data.length, windowSize.height, windowSize.width]);

    /*
      Hack: If data loaded is too short to produce a scrollbar and there is more data,
      trigger loadMore so that InfiniteScroll loads the next batch.
    */

    useEffect(() => {
        if (JSON.stringify(selectedItems) !== JSON.stringify(previousSelectedRef.current)) {
            previousSelectedRef.current = selectedItems;
        }
    }, [selectedItems]);

    const getLastSelectedItemIndex = useCallback((): number => {
        if (selectedItems !== undefined) {
            if (selectedItems === "all") {
                return data.length - 1;
            }
            if ("inc" in selectedItems) {
                return data.findIndex((item) => item._id === selectedItems.inc[selectedItems.inc.length - 1]);
            }
            if ("exc" in selectedItems) {
                return data.findIndex((_, idx, arr) => !selectedItems.exc.includes(arr[arr.length - 1 - idx]._id));
            }
        }
        return 0;
    }, [selectedItems, data]);

    useEffect(() => {
        const handleKeyUp = (e: KeyboardEvent) => {
            if (disableSelection || disableAllSelection) return;
            if (e.key === "Shift") {
                setIsShiftDown(false);
            }
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (disableSelection || disableAllSelection) return;
            if (e.key === "Shift" && !isShiftDown) {
                setIsShiftDown(true);
            }
        };

        const handleWindowBlur = () => {
            setIsShiftDown(false);
            setLastIndexToggled(getLastSelectedItemIndex());
        };

        document.addEventListener("keyup", handleKeyUp);
        document.addEventListener("keydown", handleKeyDown);
        window.addEventListener("blur", handleWindowBlur);
        return () => {
            document.removeEventListener("keyup", handleKeyUp);
            document.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("blur", handleWindowBlur);
        };
    }, [disableSelection, disableAllSelection, isShiftDown, getLastSelectedItemIndex]);

    const addSelectedItems = (items: Array<string>): void => {
        if (selectedItems !== undefined && onItemsSelection !== undefined && selectedItems !== "all") {
            if ("inc" in selectedItems) {
                const newIncSelected = Array.from(new Set([...selectedItems.inc, ...items]));
                onItemsSelection({ inc: newIncSelected });
            }
            if ("exc" in selectedItems) {
                const newExcSelected = selectedItems.exc.filter((itemId) => !items.includes(itemId));
                let selectionResult: { inc: Array<string> } | { exc: Array<string> } | "all";
                if (newExcSelected.length) {
                    selectionResult = { exc: newExcSelected };
                } else if (isAllExclusionMode) {
                    selectionResult = "all";
                } else {
                    selectionResult = { inc: [] };
                }
                onItemsSelection(selectionResult);
            }
        }
    };

    const removeSelectedItems = (items: Array<string>): void => {
        if (selectedItems !== undefined && onItemsSelection !== undefined) {
            if (selectedItems === "all") {
                if (!isAllExclusionMode) {
                    setIsAllExclusionMode(true);
                }
                onItemsSelection({ exc: items });
            } else if ("inc" in selectedItems) {
                const newIncItems = selectedItems.inc.filter((itemId) => !items.includes(itemId));
                onItemsSelection({ inc: newIncItems });
            } else if ("exc" in selectedItems) {
                if (!isAllExclusionMode && previousSelectedRef.current === "all") {
                    setIsAllExclusionMode(true);
                }
                const newExcSelected = Array.from(new Set([...selectedItems.exc, ...items]));
                onItemsSelection({ exc: newExcSelected });
            }
        }
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, itemId: string): void => {
        const { checked } = e.target;
        const newIndex = data.findIndex((item) => item._id === itemId);
        if (isShiftDown) {
            const lastIndex = lastIndexToggled;
            const startIndex = Math.min(newIndex, lastIndex);
            const endIndex = Math.max(newIndex, lastIndex);
            const changedItems = data.slice(startIndex, endIndex + 1).map((item) => item._id);
            if (checked) addSelectedItems(changedItems);
            else removeSelectedItems(changedItems);
        } else if (checked) {
            addSelectedItems([itemId]);
            setLastIndexToggled(newIndex);
        } else {
            removeSelectedItems([itemId]);
            setLastIndexToggled(getLastSelectedItemIndex());
            e.target.blur();
        }
    };

    const handleAllSelected = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { checked } = e.target;
        if (onItemsSelection !== undefined) {
            onItemsSelection(checked ? "all" : { inc: [] });
        }
        if (!checked && isAllExclusionMode) {
            setIsAllExclusionMode(false);
        }
    };

    const handleCloseBulkbar = () => {
        if (onItemsSelection !== undefined) {
            onItemsSelection({ inc: [] });
        }
    };

    const showSelections =
        selectedItems !== undefined &&
        (showSelection ||
            selectedItems === "all" ||
            ("inc" in selectedItems && selectedItems.inc.length > 0) ||
            ("exc" in selectedItems && selectedItems.exc.length > 0));

    const selectedCount = useMemo(() => {
        if (selectedItems === undefined) {
            return totalCount;
        }
        if (selectedItems === "all") {
            return totalCount;
        }
        if ("inc" in selectedItems) {
            return selectedItems.inc.length;
        }
        return totalCount - (selectedItems.exc?.length ?? 0);
    }, [selectedItems, totalCount]);

    const handleClickRow = (checked: boolean, itemId: string) => {
        const newIndex = data.findIndex((item) => item._id === itemId);
        if (checked) addSelectedItems([itemId]);
        else removeSelectedItems([itemId]);
        setLastIndexToggled(newIndex);
    };

    const displayRow = (item: Item, index: number) => {
        const checked =
            selectedItems !== undefined &&
            (selectedItems === "all" ||
                ("inc" in selectedItems && selectedItems.inc.includes(item._id)) ||
                ("exc" in selectedItems && !selectedItems.exc.includes(item._id)));

        return (
            <React.Fragment key={item._id || index}>
                <StyledListRow boxShadowOnRowHover={boxShadowOnRowHover} bgColorOnHover={bgColorOnRowHover} style={rowCustomStyle} noHover={noHover}>
                    {selectedItems !== undefined && !disableSelection && (
                        <div className={`list-select ${showSelections ? "show-selection" : ""}`}>
                            <Checkbox onChange={(e) => handleCheckboxChange(e, item._id)} checked={checked} />
                        </div>
                    )}
                    <div className="list-row-content">
                        {renderItem(
                            item,
                            {
                                handleClickRow: () => {
                                    if (selectedItems !== undefined && !disableSelection) {
                                        handleClickRow(!checked, item._id);
                                    }
                                },
                            },
                            index,
                        )}
                    </div>
                </StyledListRow>
                {index !== data.length - 1 && divider}
            </React.Fragment>
        );
    };

    const Header = (
        <StyledListRow hasVerticalScrollbars={hasVerticalScrollbars} style={rowCustomStyle}>
            {selectedItems !== undefined && !disableSelection && !disableAllSelection && (
                <div className={`list-select ${data.length === 0 ? "hidden" : "show-selection"}`}>
                    <Checkbox
                        checked={selectedItems === "all" || "exc" in selectedItems}
                        indeterminate={selectedItems !== "all" && "exc" in selectedItems}
                        onChange={handleAllSelected}
                        style={{ margin: "0px" }}
                    />
                </div>
            )}
            {header && <div className="list-row-content">{header}</div>}
        </StyledListRow>
    );

    const shouldDisplayHeader = useMemo(
        () => !!header || (selectedItems !== undefined && !disableAllSelection && !disableSelection),
        [header, disableAllSelection, disableSelection, selectedItems],
    );

    const emptyListContent = useMemo(() => (emptyList ? { EmptyPlaceholder: () => <> {emptyList} </> } : null), [emptyList]);

    const loaderContent = useMemo(() => (hasMore ? { Footer: () => <> {loader} </> } : null), [loader, hasMore]);

    return (
        <StyledList {...attributes} direction="column" gap={itemSpacing} itemSpacing={itemSpacing} ref={popoverRef}>
            {bulkbarContent !== undefined && (
                <Bulkbar selectedItemsTitle={selectedItemsTitle} selectedCount={selectedCount} onClose={handleCloseBulkbar} noSelectedCountTag>
                    {bulkbarContent}
                </Bulkbar>
            )}
            {shouldDisplayHeader && Header}
            <StyledListContainer ref={containerRef} id={containerId} itemSpacing={virtualized ? itemSpacing : undefined} overflowY={overflowY}>
                {virtualized ? (
                    <Virtuoso
                        className="virtualized-list"
                        style={{ height: "100%" }}
                        endReached={hasMore ? loadMore : undefined}
                        totalCount={totalCount}
                        data={data}
                        itemContent={(index, item) => displayRow(item, index)}
                        components={{
                            ...loaderContent,
                            ...emptyListContent,
                        }}
                    />
                ) : (
                    <InfiniteScroll
                        className="item-list"
                        key={containerRef.current}
                        next={containerRef.current?.id && loadMore}
                        hasMore={containerRef.current?.id && hasMore}
                        scrollThreshold={threshold}
                        loader={loader}
                        dataLength={data.length}
                        scrollableTarget={containerRef?.current?.id}
                    >
                        {!data.length && emptyList ? emptyList : data.map((item, index) => displayRow(item, index))}
                    </InfiniteScroll>
                )}
            </StyledListContainer>
        </StyledList>
    );
};
